import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radio, Music, ArrowUpRight, Headphones } from "lucide-react";

import { devLog } from "@/utils/devLog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 15000;

export default function NowPlayingPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [radiosRes, npRes] = await Promise.all([
        fetch(`${API}/radios`).then((r) => r.json()),
        fetch(`${API}/now-playing-all`).then((r) => r.json()),
      ]);
      const radios = radiosRes.radios || [];
      const npByRadio = Object.fromEntries(
        (npRes.items || []).map((it) => [it.radio_id, it])
      );
      const merged = radios
        .filter((r) => !r.placeholder)
        .map((r) => ({
          ...r,
          np: npByRadio[r.id] || null,
        }));
      setItems(merged);
    } catch (e) {
      // On garde les anciennes données affichées ; log dev uniquement.
      devLog("[NowPlaying] fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  return (
    <div className="mw-app np-page">
      <div className="np-bg-grain" aria-hidden="true" />

      <header className="np-header">
        <Link to="/" className="mw-brand">
          <span className="mw-brand-mark" aria-hidden="true">
            <Radio size={18} strokeWidth={2.2} />
          </span>
          <span className="mw-brand-text">
            <span className="mw-brand-name">Media</span>
            <span className="mw-brand-name mw-brand-name-accent">WebRadio</span>
          </span>
        </Link>
        <div className="np-header-meta">
          <span className="np-dot" /> Live · {items.length} radios
        </div>
      </header>

      <main className="np-main">
        <div className="np-intro">
          <span className="mw-section-eyebrow">Now Playing · Live</span>
          <h1 className="np-h1">
            Ce qui passe en ce moment <span className="mw-h1-accent">sur nos ondes</span>
          </h1>
          <p className="np-lead">
            Découvrez en temps réel les titres diffusés sur l'ensemble du réseau
            MediaWebRadio. Cliquez sur une carte pour ouvrir le mur WallTunes et
            écouter directement.
          </p>
        </div>

        {loading && items.length === 0 ? (
          <div className="admin-loading">Chargement…</div>
        ) : (
          <div className="np-grid" data-testid="np-grid">
            {items.map((r) => {
              const art = r.np?.artwork || r.logo;
              const artist = r.np?.artist || "";
              const title = r.np?.title || "";
              const hasTrack = artist && title;
              return (
                <Link
                  to="/wall"
                  key={r.id}
                  className="np-card"
                  style={{ "--np-accent": r.color }}
                  data-testid={`np-card-${r.id}`}
                >
                  <div className="np-cover">
                    <img src={art} alt={r.name} loading="lazy" />
                    <div className="np-cover-overlay">
                      <Headphones size={22} />
                      <span>Écouter</span>
                    </div>
                  </div>
                  <div className="np-info">
                    <div className="np-radio-name">{r.name}</div>
                    {hasTrack ? (
                      <>
                        <div className="np-title" title={title}>
                          <Music size={12} /> {title}
                        </div>
                        <div className="np-artist" title={artist}>{artist}</div>
                      </>
                    ) : (
                      <div className="np-empty">— On air —</div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="np-footer-cta">
          <Link to="/wall" className="mw-btn mw-btn-primary">
            Ouvrir WallTunes <ArrowUpRight size={16} />
          </Link>
          <Link to="/" className="mw-btn mw-btn-ghost">
            Découvrir MediaWebRadio
          </Link>
        </div>
      </main>
    </div>
  );
}
